import { useEffect, useState, useCallback } from "react";
import { loadAsyncScript } from "../utils/helpers/load-async-script";
import { LOCAL_STORAGE_KEYS, NUMBER } from "../constants/app-constants";
import jwtDecode from "jwt-decode";
import { AUTH_ADDRESS } from "../constants/api-constants";
import { getItem, setItem } from "../utils/helpers/storage-crud";
import { useSelector } from "react-redux";

const useGoogleOneTap = ({ onSuccess, onError, verifySignInConnect = () => {}, type, isIdleLoaded }) => {
    const {isLoggedIn} = useSelector((state) => state.user);
    const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
    const handleCredentialResponse = useCallback(async ({ credential }) => {
        try {
            const {email, name} = jwtDecode(credential);
            const payload = {
                token: credential,
                username: email,
                addressType: AUTH_ADDRESS.GOOGLE
            };
            const userProfileData = {
                name
            };
            setItem(userProfileData, LOCAL_STORAGE_KEYS.userProfile);
            const profileResponse = await verifySignInConnect(payload);
            onSuccess({...profileResponse, userId: payload.username}, payload.addressType);

        } catch (error) {
            onError(error);
        }
    }, [onError, onSuccess, verifySignInConnect]);

    const initializeGoogleSignIn = useCallback(() => {
        const userProfile = getItem(LOCAL_STORAGE_KEYS.userProfile);
        if (type !== "desktop" || !isIdleLoaded || userProfile) {
            return;
        }
        const { google } = window;
        if (google?.accounts?.id) {
            try {
                google.accounts.id.initialize({
                    client_id: process.env.GOOGLE_CLIENT_ID,
                    callback: handleCredentialResponse,
                    auto_select: false,
                    cancel_on_tap_outside: false
                });

                setIsGoogleLoaded(true);

                google.accounts.id.prompt();
            } catch (error) {
                onError(error);
            }
        } else {
            window.setTimeout(() => {
                if (window.google?.accounts?.id && !isGoogleLoaded) {
                    initializeGoogleSignIn();
                }
            }, NUMBER.THOUSAND);
        }
    }, [handleCredentialResponse, isGoogleLoaded, isIdleLoaded, onError, type]);

    const retryInitialization = useCallback(() => {
        window.setTimeout(() => {
            if (window.google?.accounts?.id && !isGoogleLoaded) {
                initializeGoogleSignIn();
            }
        }, NUMBER.THOUSAND);
    }, [initializeGoogleSignIn, isGoogleLoaded]);

    useEffect(() => {
        // Skip initialization if user is already logged in
        const userProfile = getItem(LOCAL_STORAGE_KEYS.userProfile);
        if (isLoggedIn || userProfile) {
            return;
        }
        // Check if Google is already loaded
        if (window.google?.accounts?.id) {
            initializeGoogleSignIn();
            return;
        }
        // Load the script
        loadAsyncScript(
            "google-platform",
            "https://accounts.google.com/gsi/client",
            () => {
                // Add a small delay to ensure everything is ready
                window.setTimeout(() => {
                    initializeGoogleSignIn();
                }, NUMBER.HUNDRED);
                // Also set up a retry mechanism
                retryInitialization();
            }
        );
    }, [initializeGoogleSignIn, isLoggedIn, retryInitialization]);
    return {
        isGoogleLoaded
    };
};

export default useGoogleOneTap;
